import React from 'react';
import { Filter, Edit2, X } from 'lucide-react';
import { trackButtonClick } from '../../services/analyticsService';
import { supabase } from '../../services/supabase';
import { TABLE_NAMES } from '../../constants';
import { formatShortJournalDate, getLastUpdated, isLivingDoc } from '../../utils/journalPosts';

const BlogSidebar = ({ 
  priorPosts, 
  allTags, 
  allYears, 
  setFilter, 
  filters,
  isAdmin,
  setMessage,
  onPostUpdated,
  onPostClick,
  onEditPost
}) => {
  const isFiltered = filters.tag || filters.year;

  const handleClear = () => {
    trackButtonClick({
      id: 'clear-filter-button',
      text: 'Clear filters',
      type: 'filter',
      page: 'journal',
      additionalData: { 
        cleared_tag: filters.tag,
        cleared_year: filters.year 
      }
    });
    setFilter({ tag: null, year: null });
  };

  const handleSelectTag = (tag) => {
    const isDeselecting = filters.tag === tag;
    trackButtonClick({
      id: `filter-tag-${tag}`,
      text: tag,
      type: 'filter',
      page: 'journal',
      additionalData: { 
        filter_type: 'tag',
        action: isDeselecting ? 'deselect' : 'select',
        tag: tag
      }
    });
    setFilter(prev => ({ ...prev, tag: prev.tag === tag ? null : tag }));
  };

  const handleSelectYear = (year) => {
    const isDeselecting = filters.year === year;
    trackButtonClick({
      id: `filter-year-${year}`,
      text: year.toString(),
      type: 'filter',
      page: 'journal',
      additionalData: { 
        filter_type: 'year',
        action: isDeselecting ? 'deselect' : 'select',
        year: year
      }
    });
    setFilter(prev => ({ ...prev, year: prev.year === year ? null : year }));
  };

  return (
    <div className="panel p-6 space-y-5">
      <div className="flex items-center border-b border-line pb-3">
        <h3 className="font-serif text-lg font-semibold tracking-tight text-stone-900 flex items-center">
          <Filter className="w-4 h-4 mr-1.5 text-stone-600" />
          Other Articles
        </h3>
        {isFiltered && (
          <button
            onClick={handleClear}
            className="ml-auto text-xs text-gray-500 hover:text-gray-700 transition"
          >
            Clear filters
          </button>
        )}
      </div>

      <div className="divide-y divide-line">
        {priorPosts.length > 0 ? (
          priorPosts.map(post => (
            <div key={post.id} className="py-3 first:pt-0 last:pb-0 group">
              <div className="flex items-start justify-between">
                <div
                  className="flex-1 cursor-pointer"
                  onClick={() => {
                    if (onPostClick) {
                      onPostClick(post);
                      trackButtonClick({
                        id: `view-post-${post.id}`,
                        text: post.title,
                        type: 'blog_post',
                        page: 'journal',
                        additionalData: { post_id: post.id }
                      });
                    }
                  }}
                >
                  <h4 className="font-serif text-base font-medium text-stone-800 group-hover:text-stone-950 transition">
                    {post.title}
                  </h4>
                  <div className="flex flex-wrap items-center gap-2 mt-1">
                    <p className="text-xs text-gray-500">
                      {formatShortJournalDate(getLastUpdated(post))}
                    </p>
                    {isLivingDoc(post) && (
                      <span className="px-2 py-0.5 text-[10px] font-medium bg-indigo-100 text-indigo-700 rounded-full">
                        Living doc
                      </span>
                    )}
                  </div>
                </div>
                {isAdmin && (
                  <div className="flex gap-1 ml-2 opacity-0 group-hover:opacity-100 transition">
                    <button
                      onClick={() => onEditPost && onEditPost(post)}
                      className="p-1 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded transition"
                      title="Edit post"
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>
                    <button
                      onClick={async () => {
                        if (!window.confirm('Are you sure you want to delete this journal entry? This action cannot be undone.')) {
                          return;
                        }
                        try {
                          const { error } = await supabase
                            .from(TABLE_NAMES.BLOG_POSTS)
                            .delete()
                            .eq('id', post.id);
                          if (error) throw error;
                          setMessage("Journal entry deleted!");
                          if (onPostUpdated) onPostUpdated();
                        } catch (error) {
                          console.error("Error deleting post: ", error);
                          setMessage("Failed to delete post.");
                        }
                        setTimeout(() => setMessage(''), 3000);
                      }}
                      className="p-1 text-red-600 hover:text-red-800 hover:bg-red-50 rounded transition"
                      title="Delete post"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))
        ) : (
          <p className="text-sm text-gray-500 py-2">No other entries match the current filter.</p>
        )}
      </div>

      <div className="pt-4 border-t border-line">
        <h4 className="font-serif text-base font-semibold text-stone-900 mb-3">Filter by Topic</h4>
        <div className="flex flex-wrap gap-2">
          {allTags.sort().map(tag => (
            <button
              key={tag}
              onClick={() => handleSelectTag(tag)}
              className={`px-3 py-1 text-xs font-medium rounded-full
                ${filters.tag === tag
                  ? 'bg-stone-900 text-white'
                  : 'bg-stone-100 text-stone-600 hover:bg-stone-200'
                }`}
            >
              {tag}
            </button>
          ))}
        </div>
      </div>

      <div className="pt-4 border-t border-line">
        <h4 className="font-serif text-base font-semibold text-stone-900 mb-3">Filter by Year</h4>
        <div className="flex flex-wrap gap-2">
          {allYears.map(year => (
            <button
              key={year}
              onClick={() => handleSelectYear(year)}
              className={`px-3 py-1 text-sm font-medium rounded-full
                ${filters.year === year
                  ? 'bg-stone-900 text-white'
                  : 'bg-stone-100 text-stone-600 hover:bg-stone-200'
                }`}
            >
              {year}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default BlogSidebar;
